/**
 * Surfaces brûlées d'EFFIS Copernicus, converties en anneaux exploitables.
 *
 * Deux quantités bien distinctes cohabitent désormais dans l'application, et
 * les confondre serait une faute :
 *
 * - Les **zones de feu** viennent de VIIRS et MODIS. Elles disent ce qui est
 *   CHAUD maintenant, donc ce qui fume maintenant. C'est de là, et de là seule,
 *   que sortent le verdict et le trajet du panache.
 * - Les **surfaces brûlées** viennent d'EFFIS. Elles disent ce qui A BRÛLÉ,
 *   souvent plusieurs jours plus tôt, et ce qui a brûlé ne fume plus. Elles ne
 *   doivent jamais toucher au verdict.
 *
 * L'écart n'est pas anecdotique : sur l'incendie du massif landais, le contour
 * des empreintes chaudes couvrait 187 km² quand EFFIS annonçait 45 805 hectares
 * de surface brûlée, soit 458 km². Nourrir le panache avec le second reviendrait
 * à faire fumer des cendres froides.
 *
 * Ce module ne connaît ni fetch ni horloge : il reçoit la réponse déjà lue et
 * l'instant courant en paramètre.
 */
import { BURNT_COORD_MARGIN_DEG, BURNT_MAX_AREAS } from './constants';
import { FRANCE_BOX, FRANCE_RINGS } from './france';
import { isInsideRings } from './geo';
import type { BurntArea, LatLonTuple, Ring } from './types';

const MIN_LAT = FRANCE_BOX.south - BURNT_COORD_MARGIN_DEG;
const MAX_LAT = FRANCE_BOX.north + BURNT_COORD_MARGIN_DEG;
const MIN_LON = FRANCE_BOX.west - BURNT_COORD_MARGIN_DEG;
const MAX_LON = FRANCE_BOX.east + BURNT_COORD_MARGIN_DEG;

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * L'horodatage d'EFFIS est lu en UTC, explicitement.
 *
 * Le service renvoie "2026-07-22 11:55:00", sans fuseau. Passée telle quelle à
 * `new Date()`, cette chaîne est interprétée dans le fuseau du navigateur, ce
 * qui décale tout de deux heures en été. Même piège que les horodatages
 * d'Open-Meteo, déjà payé une fois.
 */
function parseUtc(raw: unknown): number | null {
  if (typeof raw !== 'string') return null;
  const normalized = raw.trim().replace(' ', 'T');
  const withZone = /[Zz]|[+-]\d{2}:?\d{2}$/.test(normalized) ? normalized : `${normalized}Z`;
  const at = Date.parse(withZone);
  return Number.isNaN(at) ? null : at;
}

/** La surface est parfois un nombre, parfois une chaîne. */
function parseHectares(raw: unknown): number | null {
  if (isNumber(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  }
  return null;
}

const inFrance = (lat: number, lon: number): boolean =>
  lat >= MIN_LAT && lat <= MAX_LAT && lon >= MIN_LON && lon <= MAX_LON;

/**
 * Dans quel ordre ce service écrit-il ses coordonnées ?
 *
 * EFFIS répond en latitude puis longitude, à l'inverse de la convention GeoJSON.
 * Coder cet ordre en dur marcherait aujourd'hui et casserait silencieusement le
 * jour où le service le corrige : les polygones se retrouveraient au large de la
 * Somalie sans que rien ne le signale.
 *
 * L'ordre est donc DÉDUIT. Les latitudes françaises vont de 41 à 51,4 et les
 * longitudes de -5,6 à 9,9 : les deux intervalles sont disjoints, donc un seul
 * ordre peut placer tous les sommets en France. Si aucun n'y parvient, la forme
 * est rejetée plutôt que dessinée n'importe où.
 */
function detectOrder(pairs: readonly (readonly number[])[]): 'lat-first' | 'lon-first' | null {
  let latFirst = true;
  let lonFirst = true;
  for (const pair of pairs) {
    const a = pair[0];
    const b = pair[1];
    if (!isNumber(a) || !isNumber(b)) return null;
    if (!inFrance(a, b)) latFirst = false;
    if (!inFrance(b, a)) lonFirst = false;
    if (!latFirst && !lonFirst) return null;
  }
  return latFirst ? 'lat-first' : lonFirst ? 'lon-first' : null;
}

/** Extrait les listes de sommets, que la géométrie soit simple ou multiple. */
function rawRings(geometry: unknown): (readonly number[])[][] {
  if (typeof geometry !== 'object' || geometry === null) return [];
  const { type, coordinates } = geometry as { type?: unknown; coordinates?: unknown };
  if (!Array.isArray(coordinates)) return [];

  if (type === 'Polygon') return [coordinates as (readonly number[])[]];
  if (type === 'MultiPolygon') {
    return (coordinates as unknown[]).flatMap((polygon) =>
      Array.isArray(polygon) ? [polygon as (readonly number[])[]] : [],
    );
  }
  return [];
}

/**
 * Convertit la réponse d'EFFIS en surfaces brûlées.
 *
 * Tolérante par construction : une entité mal formée est écartée, les autres
 * passent. Une seule géométrie invalide ne doit pas faire disparaître la couche
 * entière, d'autant que le service en publie réellement, avec des coordonnées
 * hors des bornes terrestres.
 */
export function parseBurntAreas(payload: unknown, now: number): BurntArea[] {
  if (typeof payload !== 'object' || payload === null) return [];
  const features = (payload as { features?: unknown }).features;
  if (!Array.isArray(features)) return [];

  const areas: BurntArea[] = [];
  const seen = new Set<string>();

  for (const feature of features) {
    if (typeof feature !== 'object' || feature === null) continue;
    const { properties, geometry } = feature as { properties?: unknown; geometry?: unknown };
    const props = (typeof properties === 'object' && properties !== null ? properties : {}) as
      Record<string, unknown>;

    const groups = rawRings(geometry);
    if (groups.length === 0) continue;

    const rings: Ring[] = [];
    for (const group of groups) {
      for (const raw of group) {
        if (!Array.isArray(raw) || raw.length < 3) continue;
        const pairs = raw as (readonly number[])[];
        const order = detectOrder(pairs);
        if (order === null) continue;
        rings.push(
          pairs.map((pair): LatLonTuple =>
            order === 'lat-first' ? [pair[0]!, pair[1]!] : [pair[1]!, pair[0]!],
          ),
        );
      }
    }
    if (rings.length === 0) continue;

    /*
     * Un seul sommet en France suffit.
     *
     * L'emprise envoyée au service est un rectangle, donc elle ramène aussi le
     * nord de l'Espagne et de l'Italie : le deuxième plus vaste périmètre du
     * relevé du 28 juillet 2026 était près de Ségovie, 31 628 hectares. Il
     * n'apprend rien à quelqu'un qui se demande s'il peut ouvrir sa fenêtre en
     * France, et il encombre la carte.
     *
     * Un seul sommet, et non le barycentre, pour garder les incendies
     * transfrontaliers qui mordent sur le territoire.
     */
    const touchesFrance = rings.some((ring) =>
      ring.some(([lat, lon]) => isInsideRings(lat, lon, FRANCE_RINGS)),
    );
    if (!touchesFrance) continue;

    const startedAt = parseUtc(props['initialdate']);
    // Un feu commencé dans le futur signale une donnée fausse, pas une prédiction.
    if (startedAt !== null && startedAt > now + 24 * 3_600_000) continue;

    const id = String(props['fire_id'] ?? props['id'] ?? `${rings[0]![0]![0]},${rings[0]![0]![1]}`);
    if (seen.has(id)) continue;
    seen.add(id);

    areas.push({
      id,
      rings,
      hectares: parseHectares(props['area']),
      startedAt,
      endedAt: parseUtc(props['finaldate']),
    });
  }

  /*
   * Les plus vastes d'abord, puis un plafond. Ce sont elles qui portent
   * l'information; une parcelle de deux hectares n'apprend rien à qui se demande
   * s'il peut ouvrir sa fenêtre, et cent vingt polygones suffisent largement à
   * couvrir une saison.
   */
  return areas
    .sort((a, b) => (b.hectares ?? 0) - (a.hectares ?? 0) || a.id.localeCompare(b.id))
    .slice(0, BURNT_MAX_AREAS);
}
